import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// ============================================
// Types
// ============================================

/**
 * Layout-level UI state shared across the app shell.
 *
 * ⚠ IT WAS BUILT BECAUSE THE SHELL DID NOT SURVIVE A NAVIGATION. `DashboardLayout` used to be
 * rendered INSIDE each page (88 page surfaces rendered it themselves), so two pages were two React
 * component types and React threw the whole subtree away — layout included — on every cross-page
 * move. The sidebar's open sections were the visible cost: Mantine `NavLink` with
 * `defaultOpened={isActive}` is UNCONTROLLED, so a remount re-read the default and collapsed every
 * group except the one holding the current page. That was "the menu section closes and I lose where
 * I was", and it was unfixable inside the layout.
 *
 * ✅ **That cause is gone — ROADMAP A2b #58 hoisted the shell above the route** (`AppShellLayout`,
 * named once in `App.tsx`), so `DashboardLayout` now stays mounted across every navigation between
 * pages that have it.
 *
 * ⚠⚠ THIS PROVIDER STILL EARNS ITS PLACE, AND DELETING IT WOULD BRING THE COMPLAINT BACK. The shell
 * is not immortal: it is genuinely unmounted whenever the route has no shell at all — a print sheet,
 * the create-event wizard, onboarding, the public and error screens (`BARE_SHELL_COMPONENTS`) — and
 * it is gone entirely on a reload. Open sections survive both only because they are kept here and
 * written to storage. What #58 changed is that this is now the deliberate home for state that must
 * outlive the shell, rather than the only place state could live at all.
 */
export interface LayoutUIContextValue {
  /**
   * Is this sidebar group open?
   *
   * ⚠ `activeByDefault` is what the group would do with no opinion from the user — it holds the
   * current page. An explicit toggle always wins, which is what makes "I opened Program while
   * working in Guests" survive a navigation.
   */
  isSectionExpanded: (id: string, activeByDefault: boolean) => boolean;

  /** Record the user's opinion about a group. */
  setSectionExpanded: (id: string, expanded: boolean) => void;

  /** Forget every opinion — used when the workspace changes under the sidebar. */
  resetSections: () => void;
}

// ============================================
// Persistence
// ============================================

const STORAGE_KEY = "em.layout.sections";

/**
 * ⚠ Every read and write is wrapped. Storage throws in a private window, with site data blocked,
 * and in some embedded webviews — and a sidebar that cannot remember which groups were open must
 * still render, so a failure here is never allowed to reach the tree.
 */
const loadSections = (): Record<string, boolean> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;

    return parsed && typeof parsed === "object" ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const saveSections = (sections: Record<string, boolean>) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
  } catch {
    // Per-viewer convenience only — losing it costs an extra click, never correctness.
  }
};

// ============================================
// Context
// ============================================

const LayoutUIContext = createContext<LayoutUIContextValue | null>(null);

// ============================================
// Provider
// ============================================

interface LayoutUIProviderProps {
  children: React.ReactNode;
}

export const LayoutUIProvider: React.FC<LayoutUIProviderProps> = ({ children }) => {
  const [sections, setSections] = useState<Record<string, boolean>>(loadSections);

  useEffect(() => {
    saveSections(sections);
  }, [sections]);

  const isSectionExpanded = useCallback(
    (id: string, activeByDefault: boolean): boolean => {
      const opinion = sections[id];

      return typeof opinion === "boolean" ? opinion : activeByDefault;
    },
    [sections],
  );

  const setSectionExpanded = useCallback((id: string, expanded: boolean) => {
    setSections((previous) => {
      if (previous[id] === expanded) {
        return previous;
      }

      return { ...previous, [id]: expanded };
    });
  }, []);

  const resetSections = useCallback(() => {
    setSections({});
  }, []);

  const value = useMemo<LayoutUIContextValue>(
    () => ({ isSectionExpanded, setSectionExpanded, resetSections }),
    [isSectionExpanded, setSectionExpanded, resetSections],
  );

  return <LayoutUIContext.Provider value={value}>{children}</LayoutUIContext.Provider>;
};

// ============================================
// Hook
// ============================================

export const useLayoutUI = (): LayoutUIContextValue => {
  const context = useContext(LayoutUIContext);

  if (!context) {
    throw new Error("useLayoutUI must be used within a LayoutUIProvider");
  }

  return context;
};
